import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface FormSectionProps {
  /** Section heading. Shown as a `<legend>`; in `card` variant it is
   *  screen-reader-only (the card boundary carries the visual grouping). */
  title: string;
  /** Short, inline description shown next to the heading (plain variant). */
  description?: string;
  /**
   * Visual treatment:
   *  - `plain` (default): native `<fieldset>` + `<legend>` with a top-border
   *    divider between sections. Unchanged legacy look.
   *  - `card`: each section is a rounded, padded, bordered card with a subtle
   *    shadow and no visible heading — a cleaner grouping for full-page forms.
   */
  variant?: 'plain' | 'card';
  /** Pass `false` to skip the top border + spacing (plain variant only). */
  divider?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Groups related inputs into a `<fieldset>` (a11y win — assistive tech
 * announces the legend) and gives long forms clear visual rhythm.
 *
 * `plain` keeps the original small-caps header + divider used in dialogs.
 * `card` renders the section as a standalone card (full-page form UI polish);
 * the legend stays in the DOM but is visually hidden so screen-reader users
 * still hear the grouping.
 */
export function FormSection({
  title,
  description,
  variant = 'plain',
  divider = true,
  className,
  children,
}: FormSectionProps) {
  // `min-w-0` overrides the fieldset UA default `min-inline-size: min-content`,
  // which otherwise stops it shrinking below its widest nowrap child (e.g. a
  // long select-trigger label) and blows the layout out of a dialog.
  if (variant === 'card') {
    return (
      <fieldset
        className={cn('bg-card min-w-0 rounded-xl border p-6 shadow-sm', className)}
      >
        <legend className="sr-only">
          {title}
          {description ? ` — ${description}` : ''}
        </legend>
        <div className="space-y-5">{children}</div>
      </fieldset>
    );
  }

  return (
    <fieldset
      className={cn(
        'min-w-0 space-y-3',
        divider && 'border-t pt-4 first:border-t-0 first:pt-0',
        className,
      )}
    >
      <legend className="text-sm font-semibold tracking-tight">
        {title}
        {description ? (
          <span className="text-muted-foreground ml-2 text-xs font-normal">
            — {description}
          </span>
        ) : null}
      </legend>
      <div className="space-y-4">{children}</div>
    </fieldset>
  );
}
